import React, { useState } from 'react';
import Footer from "../components/Footer";
import MayaImg from "../assets/images/main.jpeg";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid
} from '@mui/material';
import {
  Link,
  MapPin,
  Phone,
  Mail,
  Send,
  CheckCircle
} from 'lucide-react';
import { motion } from 'framer-motion';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    botcheck: '',
  });
  const [formStatus, setFormStatus] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only accept numbers
    if (name === 'phone') {
      // Only update if the value contains only digits or is empty
      if (value === '' || /^\d+$/.test(value)) {
        setFormData((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      }
    } else {
      // For other fields, update normally
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("Sending message...");

    const formPayload = {
      access_key: '0a959127-77a8-4956-874a-e8708b1eacca',
      ...formData,
    };

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formPayload),
      });

      if (response.ok) {
        setFormStatus("Message sent successfully!");
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', botcheck: '' });
      } else {
        const errorData = await response.json();
        setFormStatus(errorData.message || "Something went wrong!");
      }
    } catch (error) {
      setFormStatus("Something went wrong!");
      console.error(error);
    }

    setTimeout(() => {
      setFormStatus('');
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <div className="w-full relative min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-green-100 to-blue-100 opacity-50"></div>
      </div>

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative h-[300px] mt-[50px] md:mt-[100px] overflow-hidden"
      >
        <img
          src={MayaImg}
          className="w-full h-full object-cover brightness-[0.7]"
          alt="Maya background"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/90" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white drop-shadow-lg">
            Contact Maya Devi University
          </h1>
        </div>
      </motion.div>

      {/* Contact Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="w-11/12 max-w-7xl mx-auto font-poppins -mt-20 relative z-10 mb-[200px]"
      >
        <div className="flex flex-col items-center mt-8 mb-8">
          <Typography variant="h6" className="text-2xl text-center mb-4">
            Have a question? Use this form for general inquiries. For admission-related queries, click here
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href="https://admission.maya.edu.in/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Apply Now
          </Button>
        </div>
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">

          {/* Contact Information Card */}
          <Grid item xs={12} md={5}>
            <Card
              variant="outlined"
              className="h-full shadow-2xl bg-white/80 backdrop-blur-sm border-2 border-green-100"
            >
              <CardHeader
                title="Get in Touch"
                className="bg-gradient-to-r from-green-500 to-blue-500 text-white"
                titleTypographyProps={{
                  variant: 'h5',
                  align: 'center',
                  className: 'font-bold'
                }}
              />
              <CardContent className="space-y-6 p-6">
                {[
                  {
                    icon: <MapPin className="text-green-600" />,
                    title: 'Address',
                    content: 'Selaqui, Pharma City, Dehradun, Uttarakhand-248011, India'
                  },
                  {
                    icon: <Mail className="text-blue-600" />,
                    title: 'Email',
                    content: 'info@maya.edu.in'
                  },
                  {
                    icon: <Phone className="text-purple-600" />,
                    title: 'Phone',
                    content: '0135 – 2698602'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="p-3 bg-gray-100 rounded-full">
                      {item.icon}
                    </div>
                    <div>
                      <Typography variant="subtitle1" className="font-bold">
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.content}
                      </Typography>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Form Card */}
          <Grid item xs={12} md={6}>

            <Card
              variant="outlined"
              className="h-full shadow-2xl bg-white/80 backdrop-blur-sm border-2 border-green-100"
            >
              <CardHeader
                title="Send Us a Message"
                className="bg-gradient-to-r from-blue-500 to-green-500 text-white"
                titleTypographyProps={{
                  variant: 'h5',
                  align: 'center',
                  className: 'font-bold'
                }}
              />
              <CardContent>

                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          required
                          placeholder="Enter your name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          required
                          placeholder="Enter your phone number"
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                          helperText="Please enter numbers only"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          required
                          placeholder="Enter your email"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          required
                          placeholder="Enter subject"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          required
                          multiline
                          rows={4}
                          placeholder="Type your message here"
                        />
                      </Grid>
                    </Grid>
                    <div className="flex justify-center mt-4">
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        endIcon={<Send />}
                      >
                        Send Message
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex flex-col items-center">
                    <CheckCircle className="text-green-600 mb-2" size={48} />
                    <Typography variant="h6" className="text-green-600">
                      {formStatus}
                    </Typography>
                  </div>
                )}
                {formStatus && !isSubmitted && (
                  <Typography variant="body2" className="text-red-600 text-center mt-2">
                    {formStatus}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      </motion.div>

      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default ContactUs;